import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { AuthProvider, useAuth } from './AuthContext'
import { ToastProvider } from './Toasts'
import ErrorBoundary from './ErrorBoundary'
import Shell from './Shell'
import LoginPage from '../features/auth/LoginPage'
import DocumentsPage from '../features/upload/DocumentsPage'
import ReviewPage from '../features/review/ReviewPage'
import BillsPage from '../features/invoices/InvoicesPage'
import VendorsPage from '../features/invoices/VendorsPage'
import ExtractionStatsPage from '../features/invoices/OverviewPage'
import ContactsPage from '../features/business/ContactsPage'
import ProductsPage from '../features/business/ProductsPage'
import SalesHub from '../features/business/SalesHub'
import PipelinePage from '../features/business/PipelinePage'
import SettingsPage from '../features/business/SettingsPage'
import ReportsPage from '../features/business/ReportsPage'
import HomePage from '../features/business/HomePage'

function Gate() {
  const { me, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="auth-page">
        <div className="row muted"><span className="spinner" /> Restoring session…</div>
      </div>
    )
  }
  if (!me) return <LoginPage />

  return (
    <Shell>
      {/* Keyed by path so recovering from an error on one screen does not leave
          the boundary latched when the user navigates somewhere else. */}
      <ErrorBoundary key={location.pathname} label={location.pathname}>
        <Routes>
          {/* The inbox is the landing page: upload, watch it get read, approve. */}
          <Route path="/" element={<Navigate to="/inbox" replace />} />
          <Route path="/inbox" element={<DocumentsPage />} />
          <Route path="/inbox/:id/review" element={<ReviewPage />} />
          <Route path="/bills" element={<BillsPage />} />

          <Route path="/sales" element={<SalesHub />} />
          <Route path="/pipeline" element={<PipelinePage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/products" element={<ProductsPage />} />

          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/settings" element={<SettingsPage />} />

          {/* Secondary screens, reachable but not in the sidebar. */}
          <Route path="/overview" element={<HomePage />} />
          <Route path="/vendors" element={<VendorsPage />} />
          <Route path="/extraction" element={<ExtractionStatsPage />} />

          {/* Old paths from before the nav was trimmed. */}
          <Route path="/documents" element={<Navigate to="/inbox" replace />} />
          <Route path="/documents/:id/review" element={<LegacyReviewRedirect />} />
          <Route path="/quotations" element={<Navigate to="/sales" replace />} />
          <Route path="/sales-orders" element={<Navigate to="/sales" replace />} />
          <Route path="/invoices" element={<Navigate to="/sales" replace />} />
          <Route path="/payments" element={<Navigate to="/sales" replace />} />
          <Route path="/home" element={<Navigate to="/inbox" replace />} />

          <Route path="*" element={<Navigate to="/inbox" replace />} />
        </Routes>
      </ErrorBoundary>
    </Shell>
  )
}

/** Keeps older /documents/:id/review links working after the rename. */
function LegacyReviewRedirect() {
  const { pathname } = useLocation()
  return <Navigate to={pathname.replace('/documents/', '/inbox/')} replace />
}

export default function App() {
  return (
    <ErrorBoundary label="root">
      <ToastProvider>
        <AuthProvider>
          <Gate />
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  )
}
